import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Clock, Link2, Linkedin, MessageCircle, Share2, Twitter } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const EASE = [0.16, 1, 0.3, 1];

export default function BlogArticle() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const articles = t("blog.articles", { returnObjects: true });
  const article = articles.find((a) => a.slug === slug) || articles[0];

  useEffect(() => {
    const original = document.title;
    document.title = `${article.title} — Wadöo`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", article.excerpt);
    return () => {
      document.title = original;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [article.title, article.excerpt]);

  const shareUrl = `${window.location.origin}/blog/${article.slug}`;
  const shareText = t("blog.shareText", { title: article.title });
  const shareFull = `${shareText} ${shareUrl}`;
  const shareLinks = [
    { id: "x", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareFull)}` },
    { id: "linkedin", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { id: "whatsapp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(shareFull)}` },
  ];

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareFull);
      toast.success(t("blog.shareCopied"));
    } catch (e) {
      // clipboard unavailable
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: shareText, url: shareUrl });
      } catch (e) {
        // user dismissed the share sheet
      }
    } else {
      copyShare();
    }
  };

  const shareBtnClass =
    "w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 transition-colors duration-200 hover:border-mint/50 hover:text-mint";

  return (
    <main data-testid="blog-article-page">
      <Header />
      <article className="pt-36 pb-24 max-w-3xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
          <Link to="/blog" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-mint transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            {t("blog.back")}
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <span className="rounded-full bg-mint/10 border border-mint/20 px-3 py-1 text-[11px] font-medium text-mint">{article.tag}</span>
            <span className="text-xs text-white/35">{article.date}</span>
            <span className="inline-flex items-center gap-1 text-xs text-white/35">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              {article.readTime}
            </span>
          </div>
          <h1 className="mt-6 font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl text-balance">
            {article.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40">{t("blog.shareTitle")}</p>
            <div className="flex items-center gap-2">
              <button onClick={shareNative} data-testid="article-share-native" className={shareBtnClass} aria-label="Share">
                <Share2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
              {shareLinks.map((l) => (
                <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`article-share-${l.id}`} className={shareBtnClass} aria-label={l.id}>
                  <l.icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
              <button onClick={copyShare} data-testid="article-share-copy" className={shareBtnClass} aria-label="Copy link">
                <Link2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div className="mt-10 space-y-6">
            {article.content.map((p, i) => (
              <p key={i} className="text-base lg:text-lg text-white/65 leading-relaxed">{p}</p>
            ))}
          </div>
          <div className="mt-14 rounded-2xl border border-mint/25 bg-mint/5 p-8 text-center">
            <p className="font-display font-semibold text-white text-xl">{article.cta?.title || t("waitlist.kicker")}</p>
            <a
              href={article.cta?.href || "/#waitlist"}
              data-testid="article-cta-waitlist"
              className="mt-4 inline-flex items-center rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
            >
              {article.cta?.label || t("waitlist.cta")}
            </a>
          </div>
        </motion.div>
      </article>
      <Footer />
    </main>
  );
}
